import React, { useState, useEffect, useRef } from "react";
import "../../styles/hero.css";
import ApplyModal from './ApplyModal';

export default function HeroSection({ college }) {
  const [current, setCurrent] = useState(0);
  const [showSticky, setShowSticky] = useState(false);
  const heroRef = useRef(null);
  const [applyModalOpen, setApplyModalOpen] = useState(false);

  // Default images fallback
  const defaultImages = [
    "https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=1400&h=600&fit=crop",
    "https://images.unsplash.com/photo-1498243691581-b145c3f54a5a?w=1400&h=600&fit=crop",
    "https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=1400&h=600&fit=crop",
  ];

  // ✅ FIX: Encode filename for CSS compatibility
  const encodeImagePath = (path) => {
    if (!path || path.startsWith('http')) return path; // Don't encode external URLs
    
    const parts = path.split('/');
    const filename = parts[parts.length - 1];
    
    // Only encode if filename has spaces or special chars
    if (filename.includes(' ') || filename.includes('(') || filename.includes(')')) {
      parts[parts.length - 1] = encodeURIComponent(filename);
    }
    
    return parts.join('/');
  };

  // ✅ FIX: Helper function to normalize image paths
  const normalizeImagePath = (imagePath) => {
    if (!imagePath) return '';
    
    let normalizedPath = imagePath;
    
    // Add leading slash if needed (not for external URLs)
    if (!imagePath.startsWith('http') && !imagePath.startsWith('/')) {
      normalizedPath = `/${imagePath}`;
    }
    
    // Encode filename part
    return encodeImagePath(normalizedPath);
  };

  const heroImages = college?.galleryImages?.length > 0 
    ? college.galleryImages.map(img => normalizeImagePath(img))
    : defaultImages;

  // Debug log
  useEffect(() => {
    console.log("Hero Images:", heroImages);
    // Test first image
    if (heroImages[0]) {
      console.log("First image encoded:", heroImages[0]);
      // Try to fetch it
      fetch(heroImages[0])
        .then(res => console.log("Image fetch status:", res.status))
        .catch(err => console.error("Image fetch error:", err));
    }
  }, [heroImages]);

  // Auto-rotate hero images
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroImages.length]);

  // Show sticky button
  useEffect(() => {
    const handleScroll = () => {
      if (heroRef.current) {
        const heroBottom = heroRef.current.getBoundingClientRect().bottom;
        setShowSticky(heroBottom < 0);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleApplyNow = () => {
    if (college?.slug === 'uit-college') {
      window.open('/applyuit', '_blank', 'noopener,noreferrer');
    } else {
      window.open(
        `/apply?college=${encodeURIComponent(college?.name || 'Unknown College')}`,
        '_blank',
        'noopener,noreferrer'
      );
    }
  };

  return (
    <>
      <header className="hsec-hero-section" ref={heroRef}>
        {/* Background Slideshow */}
        <div className="hsec-hero-slideshow">
          {heroImages.map((image, idx) => (
            <div
              key={idx}
              className={`hsec-hero-slide ${idx === current ? 'hsec-active' : ''}`}
              style={{ backgroundImage: `url("${image}")` }} // ✅ Added quotes
            />
          ))}
        </div>

        {/* Logo Overlay */}
        {college?.logo && (
          <div className="hsec-hero-logo-overlay">
            <img 
              src={normalizeImagePath(college.logo)} 
              alt="Logo" 
              className="hsec-hero-logo" 
            />
          </div>
        )}

        {/* Content Overlay */}
        <div className="hsec-hero-overlay">
          <div className="hsec-hero-content">
            <h1 className="hsec-hero-title">{college?.name}</h1>
            <p className="hsec-hero-subtitle">{college?.location}</p>
          </div>

          <div className="hsec-static-cta-container">
            <button 
              className="hsec-hero-cta hsec-static-btn" 
              onClick={handleApplyNow}
            >
              Apply Now
            </button>
          </div>

          <div className="hsec-hero-dots">
            {heroImages.map((_, idx) => (
              <button
                key={idx}
                className={idx === current ? "hsec-hero-dot hsec-hero-dot--active" : "hsec-hero-dot"}
                onClick={() => setCurrent(idx)}
                aria-label={`Slide ${idx + 1}`}
              />
            ))}
          </div>
        </div>
      </header>

      {showSticky && (
        <button 
          className="hsec-sticky-apply-btn"
          onClick={handleApplyNow}
        >
          Apply Now
        </button>
      )}

      <ApplyModal
        isOpen={applyModalOpen}
        onClose={() => setApplyModalOpen(false)}
        collegeName={college?.name || "Unknown College"}
        source="college_detail_page"
      />
    </>
  );
}
